<template>
  <div class="loan-wrapper">
    <div class="spinner-container" v-if="isLoading">
      <base-spinner :is-loading="true" :is-local="true" class="spinner" />
    </div>
    <div class="loan-container" v-if="!isLoading">
      <div class="route-name">{{ $t(`message.sideMenu.${this.$route.name}`) }}</div>
      <div class="back-block" @click="back()">
        <img
          src="../../../public/custom/images/back-arrow.svg"
          class="back-img"
          alt="Back arrow"
        />
        <span class="back-btn-text">{{ $t("message.loanPage.back") }}</span>
      </div>
      <div class="loan-info-container">
        <div class="loan-details">
          <div class="loan-details-title">{{ $t("message.loanPage.details.title") }}</div>
          <div class="loan-details-list">
            <div class="loan-details-item" v-for="detail of loanDetails" :key="detail.label">
              <div class="loan-details-label">{{ detail.label }}</div>
              <div
                class="loan-details-value"
                :class="[detail.className, { 'copyable': detail.copyable }]"
                :title="detail.value"
                @click.stop="detail.copyable && copyAgreementId()"
              >
                <span>{{ detail.value }}</span>
                <svg v-if="detail.copyable" height="16" viewBox="0 0 16 16" width="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path stroke="#A7A7A7" d="M10.6673 8.59998V11.4C10.6673 13.7333 9.73398 14.6666 7.40065 14.6666H4.60065C2.26732 14.6666 1.33398 13.7333 1.33398 11.4V8.59998C1.33398 6.26665 2.26732 5.33331 4.60065 5.33331H7.40065C9.73398 5.33331 10.6673 6.26665 10.6673 8.59998Z" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
                  <path stroke="#A7A7A7" d="M14.6673 4.59998V7.39998C14.6673 9.73331 13.734 10.6666 11.4007 10.6666H10.6673V8.59998C10.6673 6.26665 9.73398 5.33331 7.40065 5.33331H5.33398V4.59998C5.33398 2.26665 6.26732 1.33331 8.60065 1.33331H11.4007C13.734 1.33331 14.6673 2.26665 14.6673 4.59998Z" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path>
                </svg>
              </div>
            </div>
          </div>
          <div class="loan-details-actions" v-if="isShowDownloadAgreementBtn">
            <base-button
              :text="$t('message.loanPage.downloadAgreementBtn')"
              :min-height="'40px'"
              :is-loading="isDownloadAgreementLoading"
              :disabled="isDownloadAgreementLoading"
              @click="downloadAgreement()"
            />
          </div>
          <div class="early-repayment-block" v-if="isEarlyRepaymentSectionVisible">
            <div class="early-repayment-title">{{ $t('message.loanPage.earlyRepayment.title') }}</div>
            <div class="early-repayment-info" v-if="earlyRepaymentNotification">
              <div class="early-repayment-row">
                <span class="early-repayment-label">{{ $t('message.loanPage.earlyRepayment.sentAt') }}:</span>
                <span class="early-repayment-value">{{ formatDate(earlyRepaymentNotification.sentAt) }}</span>
              </div>
              <div class="early-repayment-row">
                <span class="early-repayment-label">{{ $t('message.loanPage.earlyRepayment.payFrom') }}:</span>
                <span class="early-repayment-value">{{ formatDate(earlyRepaymentPayFrom) }}</span>
              </div>
            </div>
            <div class="early-repayment-actions" v-if="isEarlyRepaymentActionsVisible">
              <div
                v-if="isShowEarlyRepaymentNotifyBtn"
                class="early-repayment-action-btn w-300"
              >
                <base-button
                  :text="$t('message.profile.borrower.earlyRepayment.notifyBtn')"
                  :min-height="'40px'"
                  @click="openEarlyRepaymentNotify()"
                />
              </div>
              <div
                v-if="isShowEarlyRepaymentPayBtn"
                class="early-repayment-action-btn"
              >
                <base-button
                  :text="$t('message.profile.borrower.earlyRepayment.payBtn')"
                  :min-height="'40px'"
                  :disabled="!profile.default_card_id"
                  @click="openEarlyRepaymentPay()"
                />
              </div>
              <div
                v-if="earlyRepaymentDocumentId"
                class="early-repayment-action-btn"
              >
                <base-button
                  :text="$t('message.loanPage.earlyRepayment.downloadNotification')"
                  :min-height="'40px'"
                  :is-loading="isDownloadNotificationLoading"
                  :disabled="isDownloadNotificationLoading"
                  @click="downloadEarlyRepaymentNotification()"
                />
              </div>
            </div>
          </div>
          <div class="claim-block" v-if="isClaimSectionVisible">
            <div class="claim-title">Претензия</div>
            <div class="claim-sent-info">
              <div class="claim-sent-icon" :title="claimSentTooltip">
                <img src="../../../public/custom/images/claim.svg" alt="Claim sent">
              </div>
              <span>{{ claimSentTooltip }}</span>
            </div>
          </div>
          <div class="lawsuit-block" v-if="isLawsuitSectionVisible">
            <div class="claim-title">Исковое заявление</div>
            <div class="lawsuit-filed-info">
              <div class="lawsuit-filed-icon" :title="lawsuitFiledTooltip">
                <img src="../../../public/custom/images/legal.svg" alt="Lawsuit filed">
              </div>
              <span>{{ lawsuitFiledTooltip }}</span>
            </div>
          </div>
        </div>
        <div class="loan-details">
          <div class="loan-details-title">
            {{ profile.role === 'borrower'
              ? $t('message.loanPage.details.lenderInfoTitle')
              : $t('message.loanPage.details.borrowerInfoTitle') }}
          </div>
          <div class="loan-details-list loan-details-list--two-cols">
            <div
              class="loan-details-item"
              :class="{ 'loan-details-item--full-width': detail.fullWidth }"
              v-for="detail of counterpartyDetails"
              :key="detail.label"
            >
              <div class="loan-details-label">{{ detail.label }}</div>
              <div
                class="loan-details-value"
                :class="{ 'loan-details-value--full': detail.fullWidth }"
                :title="detail.value"
              >
                <span>{{ detail.value }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="payment-info-container payment-info-desktop">
          <div class="title">{{ $t('message.loanPage.repaymentTitle') }}</div>
          <div class="loan-notice">{{ $t("message.loanPage.notice") }}</div>
          <div class="payment-heading">
            <div class="payment-date">{{ $t('message.loanPage.items.date') }}</div>
            <div class="payment-amount">{{ $t('message.loanPage.items.amount') }}</div>
            <div class="payment-debt">{{ $t('message.loanPage.items.total') }}</div>
            <div class="payment-percent">{{ $t('message.loanPage.items.percent') }}</div>
            <div class="payment-peni">{{ $t('message.loanPage.items.peni') }}</div>
            <div class="payment-status">{{ $t('message.loanPage.items.status') }}</div>
          </div>
          <div class="payment-empty" v-if="!loanRepaymentPayments.length">{{ $t('message.loanPage.noData') }}</div>
          <div
            class="payment-item"
            v-for="payment of loanRepaymentPayments"
            :key="payment.id"
          >
            <div class="payment-date">
              {{ parsePaymentDate(payment.createdAt) }}
            </div>
            <div class="payment-amount">
              {{ parsePaymentAmount(payment, true) }}
            </div>
            <div class="payment-debt">{{ parsePaymentPrincipalDebt(payment) }}</div>
            <div class="payment-percent">{{ parsePaymentInterestDebt(payment) }}</div>
            <div class="payment-peni">{{ parsePenalty(payment) }}</div>
            <div class="payment-status">
              <span
                v-if="payment.status === 'SUCCESS'"
                class="payment-status-badge"
              >{{ $t('message.loanPage.paymentStatus.completed') }}</span>
              <span
                v-else-if="payment.status === 'FAILED'"
                class="payment-status-badge payment-status-badge--failed"
              >{{ $t('message.loanPage.paymentStatus.failed') }}</span>
              <span
                v-else-if="payment.status === 'PENDING'"
                class="payment-status-badge payment-status-badge--pending"
              >{{ $t('message.loanPage.paymentStatus.pending') }}</span>
              <span v-else>-</span>
            </div>
          </div>
        </div>

        <div class="payment-info-container payment-info-desktop">
          <div class="title">{{ $t('message.loanPage.issuanceTitle') }}</div>
          <div class="loan-notice">{{ $t("message.loanPage.notice") }}</div>
          <div class="payment-heading">
            <div class="payment-date">{{ $t('message.loanPage.items.date') }}</div>
            <div class="payment-amount">{{ $t('message.loanPage.items.amount') }}</div>
            <div class="payment-status">{{ $t('message.loanPage.items.status') }}</div>
          </div>
          <div class="payment-empty" v-if="!loanIssuancePayments.length">{{ $t('message.loanPage.noData') }}</div>
          <div
            class="payment-item"
            v-for="payment of loanIssuancePayments"
            :key="payment.id"
          >
            <div class="payment-date">
              {{ parsePaymentDate(payment.createdAt) }}
            </div>
            <div class="payment-amount">
              {{ parsePaymentAmount(payment) }}
            </div>
            <div class="payment-status">
              <span
                v-if="payment.status === 'SUCCESS'"
                class="payment-status-badge"
              >{{ $t('message.loanPage.paymentStatus.completed') }}</span>
              <span
                v-else-if="payment.status === 'FAILED'"
                class="payment-status-badge payment-status-badge--failed"
              >{{ $t('message.loanPage.paymentStatus.failed') }}</span>
              <span
                v-else-if="payment.status === 'PENDING'"
                class="payment-status-badge payment-status-badge--pending"
              >{{ $t('message.loanPage.paymentStatus.pending') }}</span>
              <span v-else>-</span>
            </div>
          </div>
        </div>

        <div class="payment-item-mobile-block">
          <div class="loan-title">{{ $t('message.loanPage.repaymentTitle') }}</div>
          <div class="loan-notice">{{ $t("message.loanPage.notice") }}</div>
          <div class="payment-empty payment-empty-mobile" v-if="!loanRepaymentPayments.length">{{ $t('message.loanPage.noData') }}</div>
          <div
            class="mobile-payment-item"
            v-for="payment of loanRepaymentPayments"
            :key="payment.id"
          >
            <div class="preview-block">
              <div class="mobile-row">
                <div class="title">{{ $t("message.loanPage.items.date") }}</div>
                <div class="data">{{ parsePaymentDate(payment.createdAt) }}</div>
              </div>
              <div class="mobile-row">
                <div class="title">{{ $t("message.loanPage.items.amount") }}</div>
                <div class="data">{{ parsePaymentAmount(payment, true) }}</div>
              </div>
              <div class="mobile-row">
                <div class="title">{{ $t("message.loanPage.items.total") }}</div>
                <div class="data">{{ parsePaymentPrincipalDebt(payment) }}</div>
              </div>
              <div class="mobile-row">
                <div class="title">{{ $t("message.loanPage.items.percent") }}</div>
                <div class="data">{{ parsePaymentInterestDebt(payment) }}</div>
              </div>
              <div class="mobile-row">
                <div class="title">{{ $t("message.loanPage.items.peni") }}</div>
                <div class="data">{{ parsePenalty(payment) }}</div>
              </div>
              <div class="mobile-row mobile-status">
                <div class="title">{{ $t('message.loanPage.items.status') }}</div>
                <div class="data">
                  <span
                    v-if="payment.status === 'SUCCESS'"
                    class="payment-status-badge"
                  >{{ $t('message.loanPage.paymentStatus.completed') }}</span>
                  <span
                    v-else-if="payment.status === 'FAILED'"
                    class="payment-status-badge payment-status-badge--failed"
                  >{{ $t('message.loanPage.paymentStatus.failed') }}</span>
                  <span
                    v-else-if="payment.status === 'PENDING' || payment.status === 'PROCESSING'"
                    class="payment-status-badge payment-status-badge--pending"
                  >{{ $t('message.loanPage.paymentStatus.pending') }}</span>
                  <span v-else>-</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="payment-item-mobile-block">
          <div class="loan-title">{{ $t('message.loanPage.issuanceTitle') }}</div>
          <div class="loan-notice">{{ $t("message.loanPage.notice") }}</div>
          <div class="payment-empty payment-empty-mobile" v-if="!loanIssuancePayments.length">{{ $t('message.loanPage.noData') }}</div>
          <div
            class="mobile-payment-item"
            v-for="payment of loanIssuancePayments"
            :key="payment.id"
          >
            <div class="preview-block">
              <div class="mobile-row">
                <div class="title">{{ $t('message.loanPage.items.date') }}</div>
                <div class="data">{{ parsePaymentDate(payment.createdAt) }}</div>
              </div>
              <div class="mobile-row">
                <div class="title">{{ $t('message.loanPage.items.amount') }}</div>
                <div class="data">{{ parsePaymentAmount(payment) }}</div>
              </div>
              <div class="mobile-row mobile-status">
                <div class="title">{{ $t('message.loanPage.items.status') }}</div>
                <div class="data">
                  <span
                    v-if="payment.status === 'SUCCESS'"
                    class="payment-status-badge"
                  >{{ $t('message.loanPage.paymentStatus.completed') }}</span>
                  <span
                    v-else-if="payment.status === 'FAILED'"
                    class="payment-status-badge payment-status-badge--failed"
                  >{{ $t('message.loanPage.paymentStatus.failed') }}</span>
                  <span
                    v-else-if="payment.status === 'PENDING'"
                    class="payment-status-badge payment-status-badge--pending"
                  >{{ $t('message.loanPage.paymentStatus.pending') }}</span>
                  <span v-else>-</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <early-repayment-pay-modal
      v-if="isEarlyRepaymentPayVisible"
      :loan-public-id="item.public_id"
      @close="closeEarlyRepaymentPay()"
      @success="onEarlyRepaymentPaySuccess()"
    />
  </div>
</template>

<script>
import BaseSpinner from "../../components/base/BaseSpinner.vue";
import BaseButton from "../../components/base/BaseButton.vue";
import EarlyRepaymentPayModal from "../../components/earlyRepayment/EarlyRepaymentPayModal.vue";
import { mapGetters } from "vuex";
import {
  isShowNotifyMenu,
  isShowEarlyPayBtn,
  isShowAwaitingIcon,
  EARLY_REPAYMENT_ACTIONS,
} from "../../helpers/earlyRepayment";

export default {
  name: "Loan",
  inject: ["$httpService"],
  components: {
    BaseSpinner,
    BaseButton,
    EarlyRepaymentPayModal,
  },
  async created() {
    const id = this.$route.params.id;

    try {
      const item = await this.$httpService.loan.getLoanById(id);
      this.item = item.data;
    } catch (error) {
      this.back();
    }

    this.isLoading = false;
  },
  data() {
    return {
      isLoading: true,
      isDownloadAgreementLoading: false,
      isDownloadNotificationLoading: false,
      isEarlyRepaymentPayVisible: false,
      item: {},
    };
  },
  methods: {
    back() {
      const backRouteName = this.$route.params.backRouteName;

      if (backRouteName) {
        this.$router.push({
          name: backRouteName,
          params: this.$route.params.backRouteParams || {}
        });
        return;
      }

      this.$router.push({ name: "profile" });
    },
    formatMoney(value) {
      return ((Number(value) || 0) / 100).toFixed(2);
    },
    formatDate(date) {
      if (!date) {
        return "-";
      }

      return new Date(date).toLocaleDateString();
    },
    formatMobilePhone(phone) {
      if (!phone) return '';

      const digits = String(phone).replace(/\D/g, '');

      return `+${digits.slice(0, 3)} (${digits.slice(3, 5)}) ${digits.slice(5, 8)} ${digits.slice(8, 10)} ${digits.slice(10, 12)}`;
    },
    createDetailField(label, value, extra = {}) {
      if (value === null || value === undefined) {
        return null;
      }

      const normalized = String(value).trim();

      if (!normalized) {
        return null;
      }

      return {
        label,
        value: normalized,
        ...extra,
      };
    },
    getEmploymentTypeLabel(employmentTypeId) {
      if (employmentTypeId === null || employmentTypeId === undefined || employmentTypeId === '') {
        return null;
      }

      const label = this.$t(`message.signUp.jobBlock.typeOfEmployment.list.${employmentTypeId}`);

      if (!label || label === `message.signUp.jobBlock.typeOfEmployment.list.${employmentTypeId}`) {
        return null;
      }

      return label;
    },
    getWorkInfoFields(workInfo) {
      if (!workInfo) {
        return null;
      }

      const isLegacyFormat = typeof workInfo.employment_type === 'string'
        || typeof workInfo.employment_type === 'number';

      if (isLegacyFormat) {
        return [
          {
            label: this.$t('message.loanPage.details.items.companyName'),
            value: workInfo.organization_name?.trim() || '-',
          },
          {
            label: this.$t('message.loanPage.details.items.employmentType'),
            value: this.getEmploymentTypeLabel(workInfo.employment_type) || '-',
          },
        ];
      }

      return [
        {
          label: this.$t('message.loanPage.details.items.companyName'),
          value: workInfo.company_name?.trim() || '-',
        },
        {
          label: this.$t('message.loanPage.details.items.employmentType'),
          value: workInfo.employment_type?.name?.trim() || '-',
        },
      ];
    },
    parsePaymentAmount(payment, isLoanRepayment = false) {
      const amount = isLoanRepayment && (payment?.type === 'manual_pay_debt' || ['FAILED', 'PENDING', 'PROCESSING'].includes(payment?.status))
        ? payment?.request?.amount
        : payment?.response?.payout?.amount;

      if (amount === undefined || amount === null || amount === '') {
        return '-';
      }

      return (+amount / 100).toFixed(2);
    },
    parsePaymentDate(date) {
      if (!date) {
        return '-';
      }

      const parsedDate = new Date(date).toLocaleDateString();
      const hours = new Date(date).getHours();
      const minutes = String(new Date(date).getMinutes()).padStart(2, '0');

      return `${parsedDate} ${hours}:${minutes}`;
    },
    parsePaymentPrincipalDebt(item) {
      if (!item?.PrincipalDebt?.length) {
        return '0.00';
      }

      return this.formatMoney(item.PrincipalDebt.map(el => el.repaid_amount).reduce((acc, el) => acc + el, 0));
    },
    parsePaymentInterestDebt(item) {
      if (!item?.InterestDebt?.length) {
        return '0.00';
      }

      return this.formatMoney(item.InterestDebt.map(el => el.repaid_amount).reduce((acc, el) => acc + el, 0));
    },
    parsePenalty(item) {
      if (!item?.Penalty?.length) {
        return '0.00';
      }

      return this.formatMoney(item.Penalty.map(el => el.repaid_amount).reduce((acc, el) => acc + el, 0));
    },
    async copyAgreementId() {
      const agreementId = String(this.item?.Agreement?.document_id || '');

      if (!agreementId) return;

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(agreementId);
        } else {
          this.copyTextFallback(agreementId);
        }
      } catch (error) {
        this.copyTextFallback(agreementId);
      }

      this.$toast.success(this.$t('message.loanPage.agreementCopied'));
    },
    async downloadAgreement() {
      const agreementId = this.item?.Agreement?.document_id;

      if (!agreementId) {
        return;
      }

      this.isDownloadAgreementLoading = true;

      try {
        const response = await this.$httpService.loan.downloadLoan(agreementId);
        const contentType = response.headers['content-type'] || 'application/pdf';
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.setAttribute('download', `${agreementId}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
      } finally {
        this.isDownloadAgreementLoading = false;
      }
    },
    copyTextFallback(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'absolute';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    },
    async reloadLoan() {
      const id = this.$route.params.id;

      try {
        const response = await this.$httpService.loan.getLoanById(id);
        this.item = response.data;
      } catch (error) {
        this.back();
      }
    },
    openEarlyRepaymentNotify() {
      this.$router.push({
        name: 'sign-early-repayment',
        params: {
          id: this.item.public_id,
          backRouteName: this.$route.params.backRouteName,
          backRouteParams: this.$route.params.backRouteParams
        }
      });
    },
    openEarlyRepaymentPay() {
      this.isEarlyRepaymentPayVisible = true;
    },
    closeEarlyRepaymentPay() {
      this.isEarlyRepaymentPayVisible = false;
    },
    async onEarlyRepaymentPaySuccess() {
      this.closeEarlyRepaymentPay();
      await this.reloadLoan();
    },
    async downloadEarlyRepaymentNotification() {
      const documentId = this.earlyRepaymentDocumentId;

      if (!documentId) {
        return;
      }

      this.isDownloadNotificationLoading = true;

      try {
        const response = await this.$httpService.loan.downloadEarlyRepaymentNotification(documentId);
        const contentType = response.headers['content-type'] || 'application/pdf';
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.setAttribute('download', `${documentId}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
      } finally {
        this.isDownloadNotificationLoading = false;
      }
    },
  },
  computed: {
    ...mapGetters(["profile"]),
    counterpartyDetails() {
      if (this.profile?.role === 'borrower') {
        const fullname = this.item?.lender?.fullname;

        return [
          {
            label: this.$t('message.loanPage.details.items.familyName'),
            value: fullname?.family_name_ru || '-',
          },
          {
            label: this.$t('message.loanPage.details.items.givenName'),
            value: fullname?.given_name_ru || '-',
          },
          {
            label: this.$t('message.loanPage.details.items.middleName'),
            value: fullname?.middle_name_ru || '-',
          },
          {
            label: this.$t('message.loanPage.details.items.email'),
            value: this.item?.lender?.email || '-',
          },
        ];
      }

      const borrower = this.item?.Borrower || this.item?.borrower;
      const nameRu = borrower?.name_ru;
      const idDocument = borrower?.id_document;
      const idDocumentSeriesNumber = idDocument?.series_number
        || [idDocument?.series, idDocument?.number].filter(Boolean).join(' ');
      const mobilePhone = borrower?.mobile_phone
        ? this.formatMobilePhone(borrower.mobile_phone)
        : null;
      const workInfo = borrower?.work_info || this.item?.work_info;
      const workInfoFields = this.getWorkInfoFields(workInfo)
        || [this.createDetailField(this.$t('message.loanPage.details.items.workPlace'), borrower?.work_place)];

      return [
        this.createDetailField(this.$t('message.loanPage.details.items.familyName'), nameRu?.family_name_ru),
        this.createDetailField(this.$t('message.loanPage.details.items.givenName'), nameRu?.given_name_ru),
        this.createDetailField(this.$t('message.loanPage.details.items.middleName'), nameRu?.middle_name_ru),
        this.createDetailField(this.$t('message.loanPage.details.items.rating'), borrower?.rating),
        this.createDetailField(this.$t('message.loanPage.details.items.nationalIdNumber'), borrower?.national_id_number),
        this.createDetailField(this.$t('message.loanPage.details.items.idDocumentType'), idDocument?.type_label),
        this.createDetailField(this.$t('message.loanPage.details.items.idDocumentSeriesNumber'), idDocumentSeriesNumber),
        this.createDetailField(this.$t('message.loanPage.details.items.idDocumentIssueDate'), idDocument?.issue_date),
        this.createDetailField(this.$t('message.loanPage.details.items.idDocumentAuthority'), idDocument?.authority),
        this.createDetailField(this.$t('message.loanPage.details.items.mobilePhone'), mobilePhone),
        ...workInfoFields,
        this.createDetailField(this.$t('message.loanPage.details.items.registrationAddress'), borrower?.registration_address, { fullWidth: true }),
        ...(this.item?.status !== 'repaid' ? [this.createDetailField(
          this.$t('message.loanPage.details.items.residenceAddress'),
          borrower?.residence_address ? borrower?.residence_address : borrower?.registration_address,
          { fullWidth: true }
        )] : []),
        this.createDetailField(this.$t('message.loanPage.details.items.email'), borrower?.email, { fullWidth: true }),
      ].filter(Boolean);
    },
    loanDetails() {
      return [
        {
          label: this.$t("message.loanPage.details.items.agreement"),
          value: this.item?.Agreement?.document_id || "-",
          copyable: true,
        },
        {
          label: this.$t("message.loanPage.details.items.issued"),
          value: this.formatDate(this.item?.createdAt),
        },
        {
          label: this.$t("message.loanPage.details.items.amount"),
          value: this.formatMoney(this.item?.got_money),
        },
        {
          label: this.$t("message.loanPage.details.items.term"),
          value: `${this.item?.Offer?.loan_term_days || 0} ${this.$t("message.loanPage.details.days")}`,
        },
        {
          label: this.$t("message.loanPage.details.items.percent"),
          value: this.item?.Offer?.annual_percent || 0,
        },
        {
          label: this.$t("message.loanPage.details.items.income"),
          value: this.formatMoney(this.item?.initial_debt),
        },
        {
          label: this.$t("message.loanPage.details.items.issuedBy"),
          value: this.formatDate(this.item?.due_date),
        },
        {
          label: this.$t("message.loanPage.details.items.peni"),
          value: this.formatMoney(this.item?.total_penalty),
        },
        {
          label: this.$t("message.loanPage.details.items.totalAmount"),
          value: this.formatMoney(this.item?.rest_debt),
        },
        ...(this.isLenderLoanView && this.item?.repaid_date ? [{
          label: this.$t('message.loanPage.details.items.repaidDate'),
          value: this.formatDate(this.item.repaid_date),
        }] : []),
        {
          label: this.$t("message.loanPage.details.items.status"),
          value: this.$t(`message.profile.borrower.activeOffers.status.${this.item?.status}`),
          className: this.item?.status,
        },
      ];
    },
    loanRepaymentPayments() {
      return this.item?.loanRepaymentPayments || [];
    },
    loanIssuancePayments() {
      return this.item?.loanIssuancePayments || [];
    },
    isShowDownloadAgreementBtn() {
      const status = this.item?.status;

      return status && status !== 'signing' && status !== 'canceled';
    },
    earlyRepayment() {
      return this.item?.earlyRepayment || null;
    },
    earlyRepaymentAction() {
      return this.earlyRepayment?.action || EARLY_REPAYMENT_ACTIONS.NONE;
    },
    earlyRepaymentNotification() {
      return this.earlyRepayment?.notification || null;
    },
    earlyRepaymentPayFrom() {
      return this.earlyRepayment?.payAvailableFrom || this.earlyRepaymentNotification?.earliestRepaymentDate;
    },
    earlyRepaymentDocumentId() {
      return this.earlyRepaymentNotification?.documentId || null;
    },
    isEarlyRepaymentSectionVisible() {
      const action = this.earlyRepaymentAction;

      if (this.profile?.role === 'lender') {
        return isShowAwaitingIcon(this.item);
      }

      if (this.profile?.role !== 'borrower') {
        return false;
      }

      if (action === EARLY_REPAYMENT_ACTIONS.AWAITING_PAY) {
        return this.earlyRepaymentNotification?.expired === false;
      }

      return action && action !== EARLY_REPAYMENT_ACTIONS.NONE && action !== EARLY_REPAYMENT_ACTIONS.UNAVAILABLE;
    },
    isEarlyRepaymentActionsVisible() {
      return this.profile?.role === 'borrower'
        || (this.profile?.role === 'lender' && Boolean(this.earlyRepaymentDocumentId));
    },
    isShowEarlyRepaymentNotifyBtn() {
      return isShowNotifyMenu(this.item);
    },
    isShowEarlyRepaymentPayBtn() {
      return isShowEarlyPayBtn(this.item);
    },
    claim() {
      return this.item?.claim || null;
    },
    lawsuit() {
      return this.item?.lawsuit || null;
    },
    isClaimSent() {
      return this.claim?.exists === true && this.claim?.status === 'sent';
    },
    claimSentTooltip() {
      const createdAt = this.claim?.created_at;
      const date = createdAt ? new Date(createdAt) : null;

      if (!date || Number.isNaN(date.getTime())) {
        return 'Претензия отправлена';
      }

      return `Претензия отправлена ${date.toLocaleDateString('ru-RU')}`;
    },
    isLawsuitFiled() {
      return this.lawsuit?.filed === true;
    },
    lawsuitFiledTooltip() {
      const filedAt = this.lawsuit?.created_at;
      const date = filedAt ? new Date(filedAt) : null;

      if (!date || Number.isNaN(date.getTime())) {
        return 'Иск подан';
      }

      return `Иск подан ${date.toLocaleDateString('ru-RU')}`;
    },
    isLenderLoanView() {
      return this.profile?.role === 'lender';
    },
    isClaimSectionVisible() {
      return this.isClaimSent;
    },
    isLawsuitSectionVisible() {
      return this.isLawsuitFiled;
    },
  },
};
</script>

<style lang="scss" scoped>
.loan-wrapper {
  @include page-wrapper-with-header-and-footer;

  padding-top: 20px;
  box-sizing: border-box;
  background-color: var(--profile-page-bgc);
}
.loan-container {
  @include base-page-container;

  flex-direction: column;
  color: #121212;
}

.loan-info-container {
  margin-bottom: 60px;
  display: flex;
  flex-direction: column;
}

.spinner-container {
  @include base-page-container;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 10%;
  color: var(--profile-text-color);
}

.spinner-container::v-deep .loader-wrapper {
  width: auto;
}

.back-block {
  width: fit-content;
  height: 70px;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.back-img {
  margin-right: 4px;
  height: 18px;
  width: 18px;
}

.back-btn-text {
  font-weight: 400;
  font-size: 16px;
  line-height: 135%;
  color: #878787;
}

.loan-title {
  margin-bottom: 30px;
  font-weight: 600;
  font-size: 26px;
  line-height: 35px;
  color: #101010;
}

.loan-details {
  margin-bottom: 40px;
  padding: 24px;
  box-sizing: border-box;
  width: 100%;
  border: 1px solid #eceef0;
  border-radius: 6px;
  background-color: #ffffff;
}

.loan-details-title {
  margin-bottom: 20px;
  font-weight: 600;
  font-size: 22px;
  line-height: 135%;
  color: #101010;
}

.loan-details-list {
  display: grid;
  grid-template-columns: repeat(5, minmax(120px, 1fr));
  gap: 18px 24px;
}

.loan-details-list--two-cols {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 40px;
}

.loan-details-list--two-cols .loan-details-value:not(.loan-details-value--full) {
  width: 100%;
  max-width: 100%;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  word-break: break-word;
}

.loan-details-actions {
  margin-top: 24px;
  width: 240px;
  max-width: 100%;
  height: 40px;
}

.early-repayment-block {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #eceef0;
}

.claim-block,
.lawsuit-block {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #eceef0;
}

.early-repayment-title,
.claim-title {
  margin-bottom: 16px;
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: #101010;
}

.claim-sent-info,
.lawsuit-filed-info {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  line-height: 22px;
  color: #121212;
}

.claim-sent-icon {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff1d5;
  border-radius: 50%;
}

.claim-sent-icon img {
  width: 15px;
  height: 15px;
}

.lawsuit-filed-icon {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffdbdb;
  border-radius: 50%;
}

.lawsuit-filed-icon img {
  width: 15px;
  height: 15px;
}

.early-repayment-info {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.early-repayment-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 16px;
  line-height: 22px;
}

.early-repayment-label {
  color: #878787;
}

.early-repayment-value {
  font-weight: 600;
  color: #121212;
}

.early-repayment-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.early-repayment-action-btn {
  width: 280px;
  max-width: 100%;
  height: 40px;
  flex-shrink: 0;
}

.w-300 {
  width: 300px;
}

.loan-details-item {
  min-width: 0;
}

.loan-details-item--full-width {
  grid-column: 1 / -1;
}

.loan-details-label {
  margin-bottom: 6px;
  font-weight: 400;
  font-size: 14px;
  line-height: 19px;
  color: #878787;
}

.loan-details-value {
  width: fit-content;
  max-width: 100%;
  font-weight: 400;
  font-size: 18px;
  line-height: 135%;
  color: #121212;
  @include overflow-elipsis;
}

.loan-details-value--full {
  width: 100%;
  max-width: 100%;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  word-break: break-word;
}

.loan-details-value.copyable {
  display: flex;
  align-items: center;
  color: #1a8d48;
  cursor: pointer;
  font-weight: 700;
}

.loan-details-value.copyable svg {
  margin-left: 4px;
  flex: 0 0 auto;
}

.loan-details-value.copyable:hover {
  color: #41BF7B;
}

.loan-details-value.copyable:hover path {
  stroke: #41BF7B;
}

.payment,
.on_due,
.current_debt,
.payment_pending {
  padding: 5.5px 15px;
  box-sizing: border-box;
  color: #cb7723;
  background: #fff1d5;
  border-radius: 6px;
}

.closed,
.paid,
.repaid {
  padding: 5.5px 15px;
  box-sizing: border-box;
  color: #21a15c;
  background: #d6f9d3;
  border-radius: 6px;
}

.expired,
.overdue,
.payment_failed {
  padding: 5.5px 15px;
  box-sizing: border-box;
  color: #c93434;
  background: #ffdbdb;
  border-radius: 6px;
}

.signing {
  padding: 5.5px 15px;
  box-sizing: border-box;
  color: #214ea1;
  background: #d3e5f9;
  border-radius: 6px;
}

.canceled {
  padding: 5.5px 15px;
  box-sizing: border-box;
  color: #6b6b6b;
  background: #eceef0;
  border-radius: 6px;
}

.loan-notice {
  margin-bottom: 20px;
  font-weight: 400;
  font-size: 14px;
  line-height: 19px;
  color: #333333;
}

.payment-info-container {
  width: 100%;
  margin-bottom: 30px;
  display: flex;
  flex-direction: column;
}

.payment-info-container .title {
  margin-bottom: 10px;
  font-weight: 600;
  font-size: 20px;
  line-height: 32px;
}

.payment-empty {
  padding: 20px;
  box-sizing: border-box;
  font-weight: 400;
  font-size: 16px;
  line-height: 135%;
  color: #878787;
}

.payment-info-desktop {
  .payment-heading,
  .payment-item {
    > div {
      flex: 1;
      min-width: 0;
      padding-right: 16px;

      &:last-child {
        padding-right: 0;
      }
    }
  }

  .payment-heading > div {
    white-space: nowrap;
  }

}

.payment-heading {
  padding: 20px;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  border-width: 1px 0;
  border-style: solid;
  border-color: #cdcdcd;
  font-weight: 400;
  font-size: 16px;
  line-height: 135%;
  color: #878787;
}

.payment-status-badge {
  display: inline-block;
  width: fit-content;
  padding: 5.5px 15px;
  box-sizing: border-box;
  color: #21a15c;
  background: #d6f9d3;
  border-radius: 6px;
  font-size: 12px;
  line-height: 100%;
  white-space: nowrap;
}

.payment-status-badge--failed {
  color: #c93434;
  background: #ffdbdb;
}

.payment-status-badge--pending {
  color: #cb7723;
  background: #fff1d5;
}

.payment-item-mobile-block {
  display: none;
  flex-direction: column;
  margin-bottom: 30px;

  &:last-child {
    margin-bottom: 0;
  }
}

.payment-empty-mobile {
  padding: 20px 0;
}

.mobile-payment-item {
  width: 100%;
  padding: 24px 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  font-size: 16px;
  line-height: 135%;
  font-weight: 400;
  color: #121212;
  background: #f8f8f8;
  border: 1px solid #cdcdcd;
  margin-bottom: 20px;
  border-radius: 4.8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.preview-block {
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.mobile-row {
  min-height: 22px;
  margin-bottom: 28px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.payment-item-mobile-block .title {
  font-weight: 600;
  font-size: 16px;
  line-height: 115%;
  color: #878787;
}

.payment-item-mobile-block .data {
  max-width: 58%;
  font-size: 16px;
  line-height: 115%;
  text-align: right;
  word-break: break-word;
}

.mobile-status .data {
  display: flex;
  justify-content: flex-end;
}

.payment-item {
  padding: 15px 20px;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #eceef0;
  font-weight: 400;
  font-size: 16px;
  line-height: 135%;
  color: #121212;
}

.route-name {
  @include route-name;
  margin-bottom: 15px;
}

@media screen and (max-width: 965px) {
  .loan-details {
    padding: 18px;
  }

  .loan-details-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .payment-info-desktop {
    display: none;
  }

  .payment-item-mobile-block {
    width: 100%;
    display: flex;
  }
}

@media screen and (max-width: 550px) {
  .loan-details-list {
    grid-template-columns: 1fr;
  }

  .loan-details-list--two-cols {
    gap: 16px;
  }
}
</style>
